import { selectLicenseProductState } from '../../../reducers/SmartDentReducer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { SmartDentAction } from '../../../actions/SmartDentAction';
import { SmartDentConstant } from '../../../actions/constants/SmartDentConstant';
import { DEFAULT_LICENSE_PRODUCT } from '../../../actions/constants/LicenseProductConstant';
import { computeDealerRemaining } from '../../../lib/smartDentQuota';
import { useDealerIdGuard } from '../../../lib/idorGuards';
import { applyCustomerLicenseFilters, filterIssueHistory } from '../../../lib/customerLicenseFilter';
import { Translate as t } from '../../../config/Translate';
import { isPcActivationEnabled } from '../../../lib/licenseProduct';
import { useLicenseProduct } from '../../../hooks/useLicenseProduct';
import {
    LICENSE_STATUS_ALL,
    LICENSE_STATUS_ACTIVE,
    LICENSE_STATUS_SUSPEND,
    LICENSE_STATUS_EXPIRED,
    LICENSE_TYPE_ALL,
    LICENSE_TYPE_INITIAL,
    LICENSE_TYPE_RENEWAL,
} from '../../../config/appConstants';
import VoucherPrintModal from './VoucherPrintModal';
import LicenseDetailModal from './LicenseDetailModal';
import PCActivationPanel from './PCActivationPanel';
import CustomerLicensesTable from '../../../components/license/CustomerLicensesTable';
import PeriodFilter, { computeRange } from '../../../components/license/PeriodFilter';
import IssueHistoryTable from '../../../components/license/IssueHistoryTable';
import IssueHistoryFilters from '../../../components/license/IssueHistoryFilters';
import LicenseListTabs from '../../../components/license/LicenseListTabs';

// BUG-237: voucher_license 소급 백필의 탭 세션당 1회 마커.
// sessionStorage 접근은 프라이빗 모드·정책 차단 등으로 throw 할 수 있다 — 그 경우
// "아직 안 함"으로 간주해 백필 자체는 계속 동작시킨다(가용성 우선, 최악이 재조회 1회).
const BACKFILL_SESSION_KEY_PREFIX = 'rg.voucherLicenseBackfill.';

// 제품별 키 — 같은 탭에서 SMARTDent 백필이 끝났다고 다른 제품 백필이 건너뛰어지지 않게 한다.
const backfillSessionKey = (product, dealerId) =>
    `${BACKFILL_SESSION_KEY_PREFIX}${(product ?? DEFAULT_LICENSE_PRODUCT).key}.${dealerId}`;

const isBackfillDoneInSession = (product, dealerId) => {
    try {
        return window.sessionStorage.getItem(backfillSessionKey(product, dealerId)) === '1';
    } catch (error) {
        return false; // 저장소 접근 불가 = 마커 없음으로 취급 (기능 차단 아님)
    }
};

const markBackfillDoneInSession = (product, dealerId) => {
    try {
        window.sessionStorage.setItem(backfillSessionKey(product, dealerId), '1');
    } catch (error) {
        // 마커 저장 실패는 다음 진입에서 한 번 더 조회하는 비용일 뿐 — 기능 영향 없음.
        console.warn('[DealerIssuePanel] backfill session marker write failed', {
            handler: 'DealerIssuePanel.markBackfillDoneInSession',
            dealerId,
            reason: error?.message ?? 'sessionStorage unavailable',
        });
    }
};

const DealerIssuePanel = ({
    packageId,
    corpId,
    dealerId,
    role,
    companyMap,
    dealerAllocation,
    issuedQrHistory,
    dealerCustomerLicenses,
    canOperate = false,
    // IMP-422 §12.1-B: 법인 자기 선택(selfUsable) override — 미전달 시 기존 동작 유지.
    remainingOverride,
    // IMP-388: 안쪽 탭 뒤에 덧붙일 탭 [{ key, label, content }] (예: 딜러 전용 바우처 템플릿).
    extraTabs,
    // IMP-388: 안쪽 탭이 바뀔 때 새 key 로 호출 — 부모가 요약 띠 등을 숨길 때 사용.
    onTabChange,
    // IMP-388: 윗줄 왼쪽에 놓을 요약(예: AllocationSummaryCard). 안쪽 탭과 무관하게 항상 보인다.
    summary = null,
    // FEAT-059: 제품(optional). 생략 = SMARTDent. PC 활성화 미지원 제품은 PC 패널을 마운트하지 않는다.
    product: productProp,
}) => {
    const dispatch = useDispatch();
    const product = useLicenseProduct(productProp);
    const pcEnabled = useMemo(
        () => isPcActivationEnabled(product, { handler: 'DealerIssuePanel' }),
        [product],
    );
    const safeExtraTabs = Array.isArray(extraTabs) ? extraTabs : [];
    const handleTabChange = useCallback((key) => {
        if (typeof onTabChange === 'function') onTabChange(key);
    }, [onTabChange]);
    const [voucherModal, setVoucherModal] = useState({ show: false, mode: 'new', existingQrs: null });
    const [detailModal, setDetailModal] = useState({ show: false, license: null });

    // SEC-006 (Phase 2, FE defense-in-depth): props 우회로 임의 dealerId 주입 차단.
    //   dealerAllocation prop (LicenseDealerTab 에서 BE 가드 통과한 allocation record) 의 sk
    //   에서 추출한 dealerId 와 props.dealerId 가 일치할 때만 dispatch 진행.
    //
    // PR #224 review:
    //   #10/#11 (Major): fail-open → fail-closed. dealerAllocation 미정 시 차단 (이전엔 통과).
    //     - Subsidiary 경로: 부모 isDealerIdAllowed 가드로 패널 자체 렌더 안 됨.
    //     - Dealer 자기 경로: GetMyDealerAllocation 응답 전 짧은 윈도우는 차단되나, 응답 후 useEffect 재실행으로 정상 동작.
    //   #12 (Major): extractDealerIdFromSkStrict 사용 — fallback (sk 원본) 통과 차단.
    //   #14: isDealerIdValid → isDealerIdAllowed (LicenseDealerTab 와 어휘 통일).
    //
    // audit: IMP-177 — useDealerIdGuard 가 mismatch 시 security.idor-guard breadcrumb 발화
    //
    // TODO(BUG-106): legacy DDB gateway → BE REST 마이그레이션 완료 후 본 가드 영구 유지 검토
    //   (defense-in-depth — BE 가드 회귀 방지용).
    // BUG-200: self-issuance(dealerId===corpId=로그인 회사 자신)는 record 기반 SEC-006
    // 가드를 바이패스한다. self 는 타 딜러 열람이 아니라 자기 회사 조회라 IDOR 위험 없음.
    // 중간법인 self 는 dealerAllocation=null(받은-레코드가 GetDealerAllocation 에서 null)
    // 이라 record 가드로는 항상 차단되어 자기발급 이력/고객 조회가 막히는 문제 해소.
    // 비-self 는 가드 유지. hook 규칙 준수를 위해 useDealerIdGuard 는 항상 무조건 호출.
    const guardAllowed = useDealerIdGuard(dealerId, dealerAllocation);
    const isSelfIssuance = dealerId != null && dealerId === corpId;
    const isDealerIdAllowed = isSelfIssuance || guardAllowed;

    // 기간 필터는 탭별로 따로 둔다 — 발급 이력(issuedAt 조회 범위) / 고객 라이선스(발급일 created 거르기).
    // IMP-161: 기본값 'today' — 일별 운영 모니터링이 주 use case.
    const [historyPeriodFilter, setHistoryPeriodFilter] = useState({ preset: 'today', from: '', to: '' });
    const [licensesPeriodFilter, setLicensesPeriodFilter] = useState({ preset: 'today', from: '', to: '' });
    const [historyTypeFilter, setHistoryTypeFilter] = useState('all');
    const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
    const [licensesStatusFilter, setLicensesStatusFilter] = useState(LICENSE_STATUS_ALL);
    const [licensesTypeFilter, setLicensesTypeFilter] = useState(LICENSE_TYPE_ALL);
    // Expires 만 'all' 유지 — 만료 시점 기준으로 'today' 는 너무 좁은 기본값 (LicenseSubsidiaryTab 과 동일).
    const [licensesExpiresFilter, setLicensesExpiresFilter] = useState({ preset: 'all', from: '', to: '' });
    // IMP-194: User ID / groupName / email 부분 일치 검색 (대소문자 무시).
    const [licensesSearchFilter, setLicensesSearchFilter] = useState('');

    const historyPeriodRange = useMemo(() => computeRange(historyPeriodFilter), [historyPeriodFilter]);
    const licensesPeriodRange = useMemo(() => computeRange(licensesPeriodFilter), [licensesPeriodFilter]);
    const expiresRange = useMemo(() => computeRange(licensesExpiresFilter), [licensesExpiresFilter]);

    const remaining = remainingOverride ?? computeDealerRemaining(dealerAllocation);

    useEffect(() => {
        if (!dealerId || !packageId || !corpId) return;
        if (!isDealerIdAllowed) return; // SEC-006: dealerAllocation 미일치 시 dispatch 차단
        // IMP-453: 자기(self) 조회일 때만 원장(2-seg) 채택 자기발급분(dealerId 미기록)도 포함.
        dispatch(SmartDentAction.GetIssuedQrsByDealer(dealerId, {
            from: historyPeriodRange.fromTs,
            to: historyPeriodRange.toTs,
            selfView: isSelfIssuance,
            product,
        }));
    }, [dispatch, packageId, corpId, dealerId, isDealerIdAllowed, isSelfIssuance, historyPeriodRange.fromTs, historyPeriodRange.toTs, product]);

    // PR #215 review (CQ Minor) — 'expired' ↔ 'all' 토글 시 동일 BE 요청 중복 방지.
    // deps 를 apiStatus 로 변경하여 'expired' / 'all' 둘 다 같은 'all' fetch 로 매핑되더라도
    // useEffect 가 재실행되지 않게 함.
    const apiStatus = licensesStatusFilter === LICENSE_STATUS_EXPIRED
        ? LICENSE_STATUS_ALL
        : licensesStatusFilter;
    useEffect(() => {
        if (!dealerId) return;
        if (!isDealerIdAllowed) return; // SEC-006
        dispatch(SmartDentAction.GetDealerCustomerLicenses(
            dealerId,
            { status: apiStatus, product },
            SmartDentConstant.GET_DEALER_CUSTOMER_LICENSES,
        ));
    }, [dispatch, dealerId, isDealerIdAllowed, apiStatus, product]);

    // BUG-237: 자기발급(self-issuance) QR 이 리딤될 때 voucher_license 행이 생성되지
    // 않아 Customer Licenses 가 비고 PC Seat Activation 패널도 숨겨지는 문제 — 소급 백필.
    // dealerId 당 1회만 실행(backfillProcessedRef) — deps 에 dealerCustomerLicenses 를
    // 넣으면 재조회 → 상태 변경 → 재실행 루프가 생기므로 의도적으로 제외.
    //
    // PR 사전리뷰 Major: ref 만으로는 컴포넌트가 재마운트될 때마다(딜러 전환 후 복귀, 탭
    // 왕복) QR 전체이력 조회 + voucher-license 목록 GET 이 무기한 반복된다. 백필은 결손이
    // 있을 때만 의미가 있고 성공하면 다음부터 후보가 0 이므로, **탭 세션당 1회**로 좁힌다.
    // F-003(#575 PR 리뷰): 마킹 기준은 `!result?.error` 가 아니라 `result?.conclusive === true`
    // — no-manager-group/QR 0건/기존목록 GET 실패/meta·POST 후보 전건 실패/잘림 의심 등
    // "결론에 도달하지 못한" 실행은 error 없이도 마킹을 보류해 다음 진입에서 재시도된다.
    const backfillProcessedRef = useRef(null);
    useEffect(() => {
        if (!dealerId || !corpId) return;
        if (!isDealerIdAllowed) return; // SEC-006
        if (!canOperate) return;
        if (dealerId !== corpId) return; // 자기발급(원장 소유자)만 대상
        if (backfillProcessedRef.current === dealerId) return; // dealerId 당 1회
        if (isBackfillDoneInSession(product, dealerId)) {
            backfillProcessedRef.current = dealerId;
            return; // 이번 탭 세션에서 이미 처리 — 재조회 비용 회피
        }
        backfillProcessedRef.current = dealerId;
        let cancelled = false;
        // Promise.resolve 로 감싼다 — thunk 는 항상 Promise 를 돌려주지만, dispatch 미들웨어
        // 구성에 따라 undefined 가 올라올 수 있어(테스트 더블 포함) `.then` deref 를 막는다.
        Promise.resolve(dispatch(SmartDentAction.BackfillUnattributedVoucherLicenses(dealerId, { corpId, product })))
            .then((result) => {
                // F-003: 언마운트로 재조회를 못 한 실행은 결론 여부와 무관하게 마킹하지 않는다
                // — cancelled 검사를 마커 쓰기보다 먼저 둔다.
                if (cancelled) return;
                // conclusive===true(결론에 도달한 실행)일 때만 세션 마킹 — no-manager-group 등
                // 일시 장애와 구분 불가한 미결론 실행은 마킹하지 않고 다음 진입에서 재시도한다.
                if (result?.conclusive === true) markBackfillDoneInSession(product, dealerId);
                if (result?.backfilled > 0) {
                    dispatch(SmartDentAction.GetDealerCustomerLicenses(
                        dealerId,
                        { status: apiStatus, product },
                        SmartDentConstant.GET_DEALER_CUSTOMER_LICENSES,
                    ));
                }
            })
            .catch((error) => {
                // thunk 는 내부에서 전 구간 try/catch 하지만, dispatch 체인 자체의 예외까지
                // unhandled 로 새어나가지 않도록 마지막 방어선을 둔다 (CLAUDE.md §8).
                console.warn('[DealerIssuePanel] voucher_license backfill dispatch failed', {
                    handler: 'DealerIssuePanel.backfillEffect',
                    dealerId,
                    reason: error?.message ?? 'unknown error',
                });
            });
        return () => { cancelled = true; };
    }, [dispatch, dealerId, corpId, isDealerIdAllowed, canOperate, apiStatus, product]);

    useEffect(() => {
        if (!dealerId || !corpId) return;
        if (!isDealerIdAllowed) return; // SEC-006
        if (!canOperate) return; // BUG-185: self-dealing 아닌 부모 대행 발행 차단 — 템플릿 조회 자체를 생략
        dispatch(SmartDentAction.GetVoucherTemplate(dealerId, { product }));
    }, [dispatch, corpId, dealerId, isDealerIdAllowed, canOperate, product]);

    const handleVoucherIssued = useCallback(() => {
        dispatch(SmartDentAction.GetIssuedQrsByDealer(dealerId, {
            from: historyPeriodRange.fromTs,
            to: historyPeriodRange.toTs,
            selfView: isSelfIssuance,
            product,
        }));
    }, [dispatch, dealerId, isSelfIssuance, historyPeriodRange.fromTs, historyPeriodRange.toTs, product]);

    // 정렬(기본 발급 시각 내림차순)은 IssueHistoryTable 의 useClientTable 이 담당.
    const filteredQrHistory = useMemo(
        () => filterIssueHistory(issuedQrHistory, { type: historyTypeFilter, status: historyStatusFilter }),
        [issuedQrHistory, historyTypeFilter, historyStatusFilter],
    );

    // IMP-149/IMP-194: 유형/만료/발급기간/만료기간/검색 필터 체인은 공용 helper (SSoT).
    const filteredCustomerLicenses = useMemo(() => applyCustomerLicenseFilters(dealerCustomerLicenses, {
        type: licensesTypeFilter,
        status: licensesStatusFilter,
        periodRange: licensesPeriodRange,
        expiresRange,
        search: licensesSearchFilter,
    }), [dealerCustomerLicenses, licensesStatusFilter, licensesTypeFilter, licensesPeriodRange, expiresRange, licensesSearchFilter]);

    const customerLicenseStatusOptions = useMemo(() => [
        { value: LICENSE_STATUS_ALL, label: `${t('common.label.status')}: ${t('common.label.all')}` },
        { value: LICENSE_STATUS_ACTIVE, label: t('license.status.active') },
        { value: LICENSE_STATUS_SUSPEND, label: t('license.status.suspended') },
        { value: LICENSE_STATUS_EXPIRED, label: t('common.status.expired') },
    ], []);

    const customerLicenseTypeOptions = useMemo(() => [
        { value: LICENSE_TYPE_ALL, label: `${t('common.field.type')}: ${t('common.label.all')}` },
        { value: LICENSE_TYPE_INITIAL, label: t('license.label.initial') },
        { value: LICENSE_TYPE_RENEWAL, label: t('license.label.renewal') },
    ], []);

    // BUG-196: 상위 조직이 하위 조직 대행 발행 금지(strict self-dealing). BUG-185 은
    // canOperate=false 면 버튼을 숨겼으나, 사용자에게 "발행 불가" 를 명확히 인지시키기
    // 위해 숨김 대신 항상 렌더 + 비활성화로 변경. 실제 발행 차단은 IssueQr thunk 가 강제.
    // 버튼 기본 여백 제거 — 다른 License 버튼의 인라인 margin 보정과 같은 방식.
    const issueButton = (
        <button
            className="btn btn-primary btn-sm"
            style={{ margin: 0 }}
            onClick={() => setVoucherModal({ show: true, mode: 'new', existingQrs: null })}
            disabled={!canOperate || (remaining.newQty <= 0 && remaining.renewQty <= 0)}
            title={!canOperate ? t('license.message.issueVoucherSelfOnly') : undefined}
        >
            {t('license.button.issueVoucher')}
        </button>
    );
    // summary 가 컴포넌트 엘리먼트(DOM 태그 아님)면 actions prop 으로 버튼을 넘겨 요약 카드 버튼 줄에 둔다.
    const summaryTakesActions = React.isValidElement(summary) && typeof summary.type !== 'string';

    return (
        <>
            {/* FEAT-033: PC Seat Activation — matched voucher 존재 시 렌더.
                BUG-185: self-dealing 아닌 부모 대행 발행 차단 — canOperate=false 면 미렌더. */}
            {/* IMP-388: 바우처 발급 + PC 활성화는 넓은 화면에서 좌우, 좁으면 위아래.
                추가 탭(바우처 템플릿 등)이 활성이면 발급·PC·기간 필터를 숨긴다. */}
            {/* 숨김은 d-none — 언마운트하면 PC 활성화 패널이 RAYTeams 재탐지를 다시 한다. */}
            {/* 윗줄: [요약 | PC 활성화]. xl 이상 좌우·같은 높이(두 카드 h-100, 동작 버튼은 각 카드 맨 아래 왼쪽), 좁으면 줄바꿈. 안쪽 탭(바우처 템플릿 포함)은 탭 줄 아래만 바꾸므로 이 줄은 항상 보인다. */}
            <div className="row g-2 mb-2">
            {/* 발행 버튼은 요약 카드 버튼 묶음([새로고침][Issue Voucher])에만 둔다. 요약이 없거나 DOM 태그면 버튼이 사라지지 않게 요약 칸에 직접 놓는다. */}
            {(summary || !summaryTakesActions) && (
                <div className="col-12 license-summary-col">
                    {summaryTakesActions
                        ? React.cloneElement(summary, { actions: issueButton })
                        : <>{summary}{issueButton}</>}
                </div>
            )}
            {canOperate && dealerCustomerLicenses.length > 0 && pcEnabled && (
                <div className="col-12 col-xl">
                    <PCActivationPanel
                        dealerCustomerLicenses={dealerCustomerLicenses}
                        companyMap={companyMap}
                        product={product}
                    />
                </div>
            )}
            </div>

            <LicenseListTabs
                onChange={handleTabChange}
                tabs={[
                    {
                        key: 'issueHistory',
                        label: t('license.label.issueHistory'),
                        content: (
                            <>
                                {/* 기간(발급 이력용) + 유형·상태를 표 위 한 줄에. 필터 줄에는 버튼을 두지 않는다. */}
                                <IssueHistoryFilters
                                    leading={<PeriodFilter value={historyPeriodFilter} onChange={setHistoryPeriodFilter} />}
                                    typeFilter={historyTypeFilter}
                                    onTypeChange={setHistoryTypeFilter}
                                    statusFilter={historyStatusFilter}
                                    onStatusChange={setHistoryStatusFilter}
                                />
                                {/* BUG-185: canOperate=false(부모 대행 발행)면 Reprint 버튼 미렌더 — IssueHistoryTable 이 게이트. */}
                                <IssueHistoryTable
                                    items={filteredQrHistory}
                                    canOperate={canOperate}
                                    onReprint={(qr) => setVoucherModal({ show: true, mode: 'reprint', existingQrs: [qr] })}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'customerLicenses',
                        label: t('license.label.customerLicenses'),
                        content: (
                            <CustomerLicensesTable
                                licenses={filteredCustomerLicenses}
                                companyMap={companyMap}
                                // BUG-185: self-dealing 아닌 부모 대행 발행 차단 — canOperate=false 면 read-only.
                                canEdit={canOperate}
                                onDetail={(lic) => setDetailModal({ show: true, license: lic })}
                                periodFilter={licensesPeriodFilter}
                                onPeriodChange={setLicensesPeriodFilter}
                                statusFilter={licensesStatusFilter}
                                statusOptions={customerLicenseStatusOptions}
                                onStatusChange={setLicensesStatusFilter}
                                typeFilter={licensesTypeFilter}
                                typeOptions={customerLicenseTypeOptions}
                                onTypeChange={setLicensesTypeFilter}
                                expiresFilter={licensesExpiresFilter}
                                onExpiresChange={setLicensesExpiresFilter}
                                searchFilter={licensesSearchFilter}
                                onSearchChange={setLicensesSearchFilter}
                            />
                        ),
                    },
                    ...safeExtraTabs,
                ]}
            />

            {/* BUG-185: 이중 게이트 — 발행 트리거(Issue Voucher/reprint)가 모두 canOperate 뒤에
                있어 현재도 안전하나, 향후 다른 트리거 추가 시 대행 발행 방지를 위해 show 도 게이트. */}
            <VoucherPrintModal
                show={canOperate && voucherModal.show}
                mode={voucherModal.mode}
                packageId={packageId}
                corpId={corpId}
                dealerId={dealerId}
                companyMap={companyMap}
                existingQrs={voucherModal.existingQrs}
                onClose={() => setVoucherModal({ ...voucherModal, show: false })}
                onIssued={handleVoucherIssued}
                remainingOverride={remainingOverride}
                product={product}
            />
            {/* BUG-185 리뷰 Minor: 대행-쓰기 defense-in-depth — canOperate 전달. */}
            <LicenseDetailModal
                show={detailModal.show}
                license={detailModal.license}
                role={role}
                canOperate={canOperate}
                companyMap={companyMap}
                onClose={() => setDetailModal({ ...detailModal, show: false })}
                onSuccess={() => {
                    setDetailModal({ ...detailModal, show: false });
                    dispatch(SmartDentAction.GetDealerCustomerLicenses(
                        dealerId,
                        { status: licensesStatusFilter, product },
                        SmartDentConstant.GET_DEALER_CUSTOMER_LICENSES,
                    ));
                }}
            />
        </>
    );
};

const mapState = (state, ownProps) => {
    const licenseState = selectLicenseProductState(state, ownProps?.product?.key);
    return {
        issuedQrHistory: licenseState.issuedQrHistory,
        dealerCustomerLicenses: licenseState.dealerCustomerLicenses,
    };
};

export default connect(mapState)(DealerIssuePanel);
